import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import type { EChartsOption } from 'echarts';

type Step = { num: string; title: string; desc: string };

const CYCLE_MS = 6200;

const STEPS: Step[] = [
  { num: '01', title: 'Name Your Program', desc: 'Define your branded identity' },
  { num: '02', title: 'Add Phone Numbers', desc: 'Register your outbound lines' },
  { num: '03', title: 'Review & Submit', desc: 'Confirm before going live' },
  { num: '04', title: 'Program Goes Live', desc: 'Appears in your dashboard' },
  { num: '05', title: 'Check Reports', desc: 'Monitor performance & metrics' },
];

@Component({
  selector: 'app-home-product-walkthrough',
  standalone: false,
  templateUrl: './home-product-walkthrough.component.html',
  styleUrl: './home-product-walkthrough.component.scss',
})
export class HomeProductWalkthroughComponent implements OnInit, OnDestroy {
  steps = STEPS;
  active = 0;
  progress = 0;
  panelEnter = true;

  step5TotalCalls = 0;
  step5AnswerRate = 0;
  step5CallbackRate = 0;

  step5ChartOptions: EChartsOption = {};
  private step5Chart: any | null = null;
  private step5Timeouts: Array<ReturnType<typeof setTimeout>> = [];

  private cycleInterval: ReturnType<typeof setInterval> | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private step5Rafs: number[] = [];

  ngOnInit(): void {
    this.startCycle();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  selectStep(index: number): void {
    this.active = index;
    this.startCycle();
    this.triggerPanelEnter();
    this.maybeStartStep5Animation();
  }

  @HostListener('window:icip-walkthrough-step', ['$event'])
  onWalkthroughStep(event: CustomEvent<number>): void {
    this.selectStep(event.detail);
  }

  private clearTimers(): void {
    if (this.cycleInterval) {
      clearInterval(this.cycleInterval);
      this.cycleInterval = null;
    }
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    this.step5Rafs.forEach((id) => cancelAnimationFrame(id));
    this.step5Rafs = [];

    this.step5Timeouts.forEach((t) => clearTimeout(t));
    this.step5Timeouts = [];
  }

  private startCycle(): void {
    this.clearTimers();

    this.progress = 0;
    let p = 0;

    this.progressInterval = setInterval(() => {
      p += 100 / (CYCLE_MS / 16);
      if (p > 100) p = 100;
      this.progress = p;
    }, 16);

    this.cycleInterval = setInterval(() => {
      p = 0;
      this.progress = 0;
      this.active = (this.active + 1) % this.steps.length;
      this.triggerPanelEnter();
      this.maybeStartStep5Animation();
    }, CYCLE_MS);
  }

  private triggerPanelEnter(): void {
    this.panelEnter = false;
    setTimeout(() => {
      this.panelEnter = true;
    }, 0);
  }

  private maybeStartStep5Animation(): void {
    if (this.active !== 4) return;

    this.step5Rafs.forEach((id) => cancelAnimationFrame(id));
    this.step5Rafs = [];

    const totalCallsTarget = 62000;
    const answerRateTarget = 74;
    const callbackRateTarget = 18.8;

    this.step5TotalCalls = 0;
    this.step5AnswerRate = 0;
    this.step5CallbackRate = 0;

    const durMs = 1100;

    this.step5ChartOptions = this.buildStep5ChartOptions();

    this.step5Timeouts.forEach((t) => clearTimeout(t));
    this.step5Timeouts = [];

    const showTipAt = (idx: number) => {
      if (!this.step5Chart) {
        this.step5Timeouts.push(setTimeout(() => showTipAt(idx), 50));
        return;
      }

      this.step5Chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: idx,
      });
    };

    this.step5Timeouts.push(setTimeout(() => showTipAt(2), 1900));
    this.step5Timeouts.push(setTimeout(() => showTipAt(3), 3000));
    this.step5Timeouts.push(setTimeout(() => showTipAt(4), 4100));

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const animateWithDelay = (
      delayMs: number,
      apply: (eased: number) => void,
    ): void => {
      const startAt = performance.now() + delayMs;

      const tick = (now: number) => {
        const t = Math.min(1, Math.max(0, (now - startAt) / durMs));
        apply(easeOutCubic(t));

        if (t < 1) {
          this.step5Rafs.push(requestAnimationFrame(tick));
        }
      };

      this.step5Rafs.push(requestAnimationFrame(tick));
    };

    const d1 = Math.round(CYCLE_MS * 0.06);
    const d2 = Math.round(CYCLE_MS * 0.16);
    const d3 = Math.round(CYCLE_MS * 0.26);

    animateWithDelay(d1, (eased) => {
      this.step5TotalCalls = Math.round(totalCallsTarget * eased);
    });

    animateWithDelay(d2, (eased) => {
      this.step5AnswerRate = Math.round(answerRateTarget * eased);
    });

    animateWithDelay(d3, (eased) => {
      this.step5CallbackRate = Math.round(callbackRateTarget * 10 * eased) / 10;
    });
  }

  onStep5ChartInit(chart: any): void {
    this.step5Chart = chart;
  }

  private buildStep5ChartOptions(): EChartsOption {
    const labels = ['Jan 01', 'Jan 08', 'Jan 15', 'Jan 22', 'Jan 29', 'Feb 05', 'Feb 12'];
    const values = [42000, 45000, 52000, 48000, 55000, 58000, 62000];

    return {
      animationDuration: 2000,
      grid: {
        left: 40,
        right: 10,
        top: 10,
        bottom: 12,
        containLabel: true,
      },
      tooltip: {
        show: true,
        trigger: 'axis',
        triggerOn: 'none',
        alwaysShowContent: true,
        confine: true,
        backgroundColor: '#ffffff',
        borderWidth: 0,
        padding: [12, 14],
        textStyle: { color: '#111827', fontFamily: 'Inter', fontSize: 12 },
        extraCssText: 'border-radius: 12px; box-shadow: 0 12px 24px rgba(15, 23, 42, 0.15);',
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#cbd5e1', width: 1 },
        },
        position: (point: number[], _params: any, dom: any, _rect: any, size: any) => {
          const viewWidth = size?.viewSize?.[0] ?? 0;
          const viewHeight = size?.viewSize?.[1] ?? 0;
          const boxWidth = size?.contentSize?.[0] ?? 0;
          const boxHeight = size?.contentSize?.[1] ?? 0;

          const margin = 8;

          let x = point[0] - boxWidth / 2;
          let y = point[1] - boxHeight - 14;

          if (x < margin) x = margin;
          if (x + boxWidth > viewWidth - margin) x = viewWidth - margin - boxWidth;

          if (y < margin) y = margin;
          if (y + boxHeight > viewHeight - margin) y = viewHeight - margin - boxHeight;

          if (dom && dom.style) {
            dom.style.pointerEvents = 'none';
          }
          return [x, y];
        },
        formatter: (params: any) => {
          const first = Array.isArray(params) ? params[0] : params;
          const label = first?.axisValueLabel ?? first?.axisValue ?? '';
          const raw = first?.data;
          const n = typeof raw === 'number' ? raw : Number(raw);
          const value = Number.isNaN(n) ? '' : new Intl.NumberFormat('en-US').format(n);
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
          const sizes = isMobile
            ? { min: 110, label: 10, value: 13, valueLh: 16, sub: 10 }
            : { min: 140, label: 12, value: 18, valueLh: 22, sub: 12 };
          return `<div style="display:flex;flex-direction:column;gap:4px;min-width:${sizes.min}px;">
            <div style="font-size:${sizes.label}px;line-height:14px;color:#6B7280;">${label}</div>
            <div style="font-weight:800;font-size:${sizes.value}px;line-height:${sizes.valueLh}px;color:#071d49;">${value} calls</div>
            <div style="font-size:${sizes.sub}px;line-height:14px;color:#94A3B8;">Branded calls delivered</div>
          </div>`;
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 65000,
        interval: 20000,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#eef2f7' } },
        axisLabel: {
          color: '#9ba5b7',
          fontFamily: 'Inter',
          fontSize: typeof window !== 'undefined' && window.innerWidth <= 600 ? 9 : 11,
          formatter: (value: number) => `${Math.round(value / 1000)}K`,
        },
      },
      series: [
        {
          type: 'line',
          data: values,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 10,
          smooth: false,
          silent: true,
          lineStyle: { width: 4, color: '#071d49' },
          areaStyle: {
            opacity: 1,
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(7, 29, 73, 0.10)' },
                { offset: 1, color: 'rgba(7, 29, 73, 0.00)' },
              ],
            },
          },
          itemStyle: {
            color: '#071d49',
            borderColor: '#071d49',
            borderWidth: 0,
          },
          emphasis: { disabled: true },
          animationDelay: (idx: number) => 200 + idx * 140,
        },
      ],
    };
  }
}
